import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IoArrowBack,
  IoDocumentTextOutline,
  IoFolderOpenOutline,
  IoFolderOutline,
  IoCloseOutline,
  IoChevronForward,
  IoChevronBack,
  IoEyeOutline,
} from 'react-icons/io5';
import { reimbursementGuideData } from '../../../data/pdfConfig';

// ─── Animation variants ───────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 130 } },
};

// ─── PDF Modal ────────────────────────────────────────────────────────────────

const PdfModal = ({ selectedPdf, documents, currentIndex, onClose, onNext, onPrev, onJumpTo }) => {
  // Lock body scroll while modal is open
  useEffect(() => {
    if (selectedPdf) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedPdf]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!selectedPdf) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="pdf-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        // z-index 99999 guarantees this sits above the ProMed navbar (~z-50)
        // position:fixed + inset-0 anchors to the viewport, not the scroll container
        style={{ position: 'fixed', inset: 0, zIndex: 99999 }}
        className="flex items-stretch justify-center bg-black/85 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 12 }}
          transition={{ type: 'spring', stiffness: 240, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          // m-4 gives breathing room from all viewport edges incl. top navbar
          className="m-4 sm:m-6 w-full max-w-6xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* ── Header bar ───────────────────────────────────────────────── */}
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Prev */}
              {documents.length > 1 && (
                <motion.button
                  onClick={onPrev}
                  className="flex-shrink-0 p-1.5 rounded-lg bg-white/15 hover:bg-white/30 text-white transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  title="Previous"
                >
                  <IoChevronBack className="text-lg" />
                </motion.button>
              )}

              {/* Icon */}
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <IoDocumentTextOutline className="text-white text-base" />
              </div>

              {/* Title */}
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold text-teal-100 uppercase tracking-widest leading-none mb-0.5">
                  Document {currentIndex + 1} of {documents.length}
                </p>
                <h3 className="text-sm font-bold text-white truncate leading-snug">
                  {selectedPdf.name}
                </h3>
              </div>

              {/* Next */}
              {documents.length > 1 && (
                <motion.button
                  onClick={onNext}
                  className="flex-shrink-0 p-1.5 rounded-lg bg-white/15 hover:bg-white/30 text-white transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  title="Next"
                >
                  <IoChevronForward className="text-lg" />
                </motion.button>
              )}
            </div>

            {/* Close */}
            <motion.button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg bg-white/15 hover:bg-red-500/80 text-white transition-colors ml-2"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title="Close (Esc)"
            >
              <IoCloseOutline className="text-xl" />
            </motion.button>
          </div>

          {/* ── Thumbnail strip ───────────────────────────────────────────── */}
          {documents.length > 1 && (
            <div className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
              {documents.map((doc, idx) => (
                <button
                  key={idx}
                  onClick={() => onJumpTo(idx)}
                  className={`flex-shrink-0 w-7 h-7 rounded-md text-[11px] font-bold transition-all border ${
                    idx === currentIndex
                      ? 'bg-teal-500 text-white border-teal-500 shadow'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-teal-400 hover:text-teal-600'
                  }`}
                  title={doc.name}
                >
                  {idx + 1}
                </button>
              ))}
              <span className="flex-shrink-0 self-center ml-3 text-[11px] text-gray-400 dark:text-gray-500 font-medium truncate max-w-[40vw]">
                {documents[currentIndex]?.name}
              </span>
            </div>
          )}

          {/* ── PDF iframe — fills all remaining height ───────────────────── */}
          <div className="flex-1 min-h-0 bg-gray-200 dark:bg-gray-950">
            <iframe
              key={selectedPdf.url}
              src={`${selectedPdf.url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
              className="w-full h-full"
              title={selectedPdf.name}
              style={{ border: 'none', display: 'block' }}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── File List View ────────────────────────────────────────────────────────────

const FileListView = ({ folder, onBack, onOpenPdf }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <motion.div
      key="file-list"
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.22 }}
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-6">
        <motion.button
          onClick={onBack}
          className="flex items-center gap-2 text-teal-600 dark:text-teal-400 font-bold hover:gap-3 transition-all group"
          whileHover={{ x: -4 }}
        >
          <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" />
          Back to Folders
        </motion.button>
        <span className="text-gray-300 dark:text-gray-600 select-none">/</span>
        <span className="text-gray-600 dark:text-gray-300 font-semibold">{folder.name}</span>
      </div>

      {/* Folder header */}
      <div className="mb-8 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
          <IoFolderOpenOutline className="text-3xl text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white">{folder.name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {folder.description} &mdash;{' '}
            <span className="font-semibold text-teal-600 dark:text-teal-400">
              {folder.documents.length} documents
            </span>
          </p>
        </div>
      </div>

      {/* File grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {folder.documents.map((doc, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            onHoverStart={() => setHoveredIndex(index)}
            onHoverEnd={() => setHoveredIndex(null)}
          >
            <motion.button
              onClick={() => onOpenPdf(doc, index)}
              className="w-full text-left group relative bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-5 hover:border-teal-400 dark:hover:border-teal-500 transition-all overflow-hidden"
              whileHover={{ y: -3, scale: 1.015 }}
              whileTap={{ scale: 0.97 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-2xl pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
                transition={{ duration: 0.16 }}
              />

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/40 dark:to-cyan-900/40 flex items-center justify-center flex-shrink-0 group-hover:from-teal-200 group-hover:to-cyan-200 transition-all">
                    <IoDocumentTextOutline className="text-xl text-teal-600 dark:text-teal-400" />
                  </div>
                  <motion.div
                    className="flex items-center gap-1 px-2.5 py-1 bg-teal-500 text-white rounded-lg text-[11px] font-bold shadow"
                    initial={{ opacity: 0, scale: 0.75 }}
                    animate={{
                      opacity: hoveredIndex === index ? 1 : 0,
                      scale: hoveredIndex === index ? 1 : 0.75,
                    }}
                    transition={{ duration: 0.13 }}
                  >
                    <IoEyeOutline className="text-sm" />
                    View
                  </motion.div>
                </div>

                <p className="text-sm font-bold text-gray-900 dark:text-white leading-snug mb-1 group-hover:text-teal-700 dark:group-hover:text-teal-300 transition-colors">
                  {doc.name}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                  PDF Document · #{index + 1}
                </p>
              </div>
            </motion.button>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

// ─── Folder Grid View ──────────────────────────────────────────────────────────

const FolderGridView = ({ folders, onSelectFolder }) => (
  <motion.div
    key="folder-grid"
    initial={{ opacity: 0, x: -40 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 40 }}
    transition={{ duration: 0.22 }}
  >
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {folders.map((folder) => (
        <motion.div key={folder.id} variants={itemVariants}>
          <motion.button
            onClick={() => onSelectFolder(folder)}
            className="w-full text-left group relative bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-7 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-xl transition-all overflow-hidden"
            whileHover={{ y: -4, scale: 1.015 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-50/0 to-cyan-50/0 group-hover:from-teal-50 group-hover:to-cyan-50/60 dark:group-hover:from-teal-900/20 dark:group-hover:to-cyan-900/10 transition-all duration-300 rounded-2xl pointer-events-none" />
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-teal-100/50 dark:bg-teal-900/20 rounded-full group-hover:scale-150 transition-transform duration-500 pointer-events-none" />

            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center mb-5 shadow-lg relative">
                <IoFolderOutline className="text-3xl text-white group-hover:opacity-0 transition-opacity absolute" />
                <IoFolderOpenOutline className="text-3xl text-white opacity-0 group-hover:opacity-100 transition-opacity absolute" />
              </div>

              <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-1 group-hover:text-teal-700 dark:group-hover:text-teal-300 transition-colors">
                {folder.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                {folder.description}
              </p>

              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-lg text-xs font-bold">
                  <IoDocumentTextOutline className="text-sm" />
                  {folder.documents.length} files
                </span>
                <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 group-hover:translate-x-1 transition-transform">
                  <IoChevronForward className="text-base" />
                </div>
              </div>
            </div>
          </motion.button>
        </motion.div>
      ))}
    </motion.div>
  </motion.div>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const ReimbursementGuide = () => {
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);

  const folders = reimbursementGuideData;

  const openPdf = (doc, index) => {
    setSelectedPdf(doc);
    setCurrentPdfIndex(index);
  };

  const closePdf = () => setSelectedPdf(null);

  const nextPdf = () => {
    const docs = selectedFolder?.documents || [];
    const next = (currentPdfIndex + 1) % docs.length;
    setCurrentPdfIndex(next);
    setSelectedPdf(docs[next]);
  };

  const prevPdf = () => {
    const docs = selectedFolder?.documents || [];
    const prev = (currentPdfIndex - 1 + docs.length) % docs.length;
    setCurrentPdfIndex(prev);
    setSelectedPdf(docs[prev]);
  };

  const jumpTo = (index) => {
    const docs = selectedFolder?.documents || [];
    setCurrentPdfIndex(index);
    setSelectedPdf(docs[index]);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 pb-12 pt-6">
      <div className="max-w-5xl mx-auto">

        {/* Page header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-4 mb-2">
            <div className="w-1.5 h-8 bg-gradient-to-b from-teal-500 to-cyan-500 rounded-full" />
            <h1 className="text-3xl font-black text-gray-900 dark:text-white">
              Reimbursement Guide
            </h1>
          </div>
          {/* <p className="ml-5 text-sm text-gray-500 dark:text-gray-400 font-medium">
            {selectedFolder
              ? `Browsing "${selectedFolder.name}" — click any document to open it`
              : 'Select a folder to browse reimbursement documents'}
          </p> */}
        </motion.div>

        {/* Content card */}
        <div className="bg-gradient-to-br from-white via-teal-50/20 to-cyan-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl border-2 border-teal-100 dark:border-teal-900/40 shadow-sm p-6 sm:p-8 min-h-[420px]">
          <AnimatePresence mode="wait">
            {selectedFolder ? (
              <FileListView
                key="files"
                folder={selectedFolder}
                onBack={() => setSelectedFolder(null)}
                onOpenPdf={openPdf}
              />
            ) : (
              <FolderGridView
                key="folders"
                folders={folders}
                onSelectFolder={setSelectedFolder}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* PDF modal — z-index 99999 beats any app navbar */}
      <PdfModal
        selectedPdf={selectedPdf}
        documents={selectedFolder?.documents || []}
        currentIndex={currentPdfIndex}
        onClose={closePdf}
        onNext={nextPdf}
        onPrev={prevPdf}
        onJumpTo={jumpTo}
      />
    </div>
  );
};

export default ReimbursementGuide;